import React, { useEffect, useRef } from "react"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Slide from "@mui/material/Slide"
import { CLOSE_FULL_SCREEN_IMAGE_MODAL } from "constants/events"
import EditIcon from "components/atoms/_icons/EditIcon"
import TrashIcon from "components/atoms/_icons/TrashIcon"
import ArrowIcon from "components/atoms/_icons/ArrowIcon"
import IconWrapper from "components/atoms/IconWrapper"
import LazyImage from "components/atoms/LazyImage"

type FullScreenSelectedImageModalProps = {
  show: boolean;
  src: string | null;
  onImageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onImageDelete: () => void;
}

export default function FullScreenSelectedImageModal(props: FullScreenSelectedImageModalProps) {
  const { show, src, onImageChange, onImageDelete } = props

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!show && inputRef.current) {
      inputRef.current.value = ""
    }
  }, [show])

  function handleBackButtonClicked() {
    document.dispatchEvent(new CustomEvent(CLOSE_FULL_SCREEN_IMAGE_MODAL))
  }

  return (
    <Slide
      id="full-screen-selected-image-modal"
      direction="left"
      in={show}
      easing={{
        enter: "ease-in-out",
        exit: "linear"
      }}
      style={{ position: "fixed", top: 0, left: 0 }}
    >
      <Container>
        <IconWrapper id="back-button" onClick={handleBackButtonClicked}>
          <ArrowIcon/>
        </IconWrapper>

        {show && src && <ImageWrapper>
            <LazyImage
                src={src}
                alt="avatar"
                theme="dark"
            />
        </ImageWrapper>}

        <BottomActionsContainer>
          <label>
            <input
              ref={inputRef}
              id="image"
              type="file"
              accept="image/*"
              onChange={onImageChange}
            />
            <IconWrapper id="edit">
              <EditIcon/>
            </IconWrapper>
          </label>
          <IconWrapper id="delete" onClick={onImageDelete}>
            <TrashIcon/>
          </IconWrapper>
        </BottomActionsContainer>
      </Container>
    </Slide>
  )
}

const Container = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-end",
  backgroundColor: theme.palette.gray["16"],
  transform: "translateX(-50%)",
  zIndex: 10,

  "& svg": {
    stroke: "#FFFFFF",
    width: 24,
    height: 24
  },

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const ImageWrapper = styled("div")(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vw",
  aspectRatio: 1,
  overflow: "hidden",
  position: "relative"
}))

const BottomActionsContainer = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px 16px",

  "& div": {
    borderRadius: "50%",
    backgroundColor: theme.palette.gray["15"]
  },

  "& input": {
    display: "none"
  }
}))
